import { useEffect, useState } from "react";

import { type TContentPart } from "../../../types/agent";
import ViewCodeModal from "../modals/ViewCodeModal";

import { extractCode } from "./codeRendererUtils";

export default function CodeRenderer({
	content,
}: {
	content: TContentPart;
}) {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [codeContent, setCodeContent] =
		useState<string>("");
	const [language, setLanguage] =
		useState<string>("python");
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(
		null
	);

	useEffect(() => {
		const controller = new AbortController();
		const fetchContent = async () => {
			setCodeContent("");
			setLoadError(null);

			// Fetch large code directly from its SAS URL when available.
			if (content.blob_link) {
				setLoading(true);
				try {
					const response = await fetch(content.blob_link, {
						signal: controller.signal,
					});
					if (!response.ok) {
						throw new Error("Failed to fetch code content");
					}
					const text = await response.text();
					if (controller.signal.aborted) {
						return;
					}
					const extracted = extractCode(text);
					setCodeContent(extracted.code);
					setLanguage(extracted.language);
					setLoading(false);
					return;
				} catch (err: unknown) {
					if (controller.signal.aborted) {
						return;
					}
					console.error(
						"Error fetching code content from blob:",
						err
					);
					setLoadError("Full code could not be loaded.");
					setLoading(false);
				}
				return;
			}

			// Inline content is used only when no full-code blob exists.
			if (content.content) {
				const extracted = extractCode(content.content);
				setCodeContent(extracted.code);
				setLanguage(extracted.language);
			}
		};

		void fetchContent();
		return () => {
			controller.abort();
		};
	}, [content]);

	if (loading) {
		return (
			<div className="view-code-button">
				Loading code...
			</div>
		);
	}

	if (loadError) {
		return (
			<div className="view-code-button">{loadError}</div>
		);
	}

	if (!codeContent) {
		return null;
	}

	return (
		<>
			<button
				className="view-code-button"
				aria-label="View code"
				title="View code"
				onClick={() => {
					setIsModalOpen(true);
				}}
			>
				<svg
					width="20"
					height="20"
					viewBox="0 0 20 20"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					className="view-code-icon"
				>
					<path
						d="M14.1666 6.66669L17.4999 10L14.1666 13.3334M5.83325 13.3334L2.49992 10L5.83325 6.66669M9.16659 15.8334L10.8333 4.16669"
						stroke="currentColor"
						strokeWidth="1.66667"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
				<span>View code</span>
			</button>
			<ViewCodeModal
				open={isModalOpen}
				onClose={() => {
					setIsModalOpen(false);
				}}
				code={codeContent}
				language={language}
			/>
		</>
	);
}
